import { APP_API_URL } from './api-config.service'
import { getCurrentRoutePageMeta } from './route-page.service'
import { tokenService } from './token.service'
import { reportUnexpectedClientError } from './client-error.service'

export type RealtimeTopic = 'badges' | 'onlineOperators' | 'applications' | 'signature' | 'users'

type RealtimeEvent = {
    type?: string
    topic?: RealtimeTopic
    event?: string
    payload?: unknown
}

type RealtimeHandler = (event: RealtimeEvent) => void

class RealtimeService {
    private static readonly RECONNECT_RESET_DELAY_MS = 60 * 1000

    private socket: WebSocket | null = null
    private reconnectTimeoutID: ReturnType<typeof setTimeout> | null = null
    private reconnectResetTimeoutID: ReturnType<typeof setTimeout> | null = null
    private heartbeatIntervalID: ReturnType<typeof setInterval> | null = null
    private reconnectAttempt = 0
    private isManualDisconnect = false
    private handlers = new Map<RealtimeTopic, Set<RealtimeHandler>>()

    subscribe = (topic: RealtimeTopic, handler: RealtimeHandler) => {
        const topicHandlers = this.handlers.get(topic) || new Set<RealtimeHandler>()
        topicHandlers.add(handler)
        this.handlers.set(topic, topicHandlers)

        this.connect()
        this.sendSubscriptions([topic])

        return () => {
            this.unsubscribe(topic, handler)
        }
    }

    connect = () => {
        if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return

        const token = localStorage.getItem('us_id')
        if (!token) return

        this.isManualDisconnect = false
        const socket = new WebSocket(this.getRealtimeUrl(), ['crm-v1', `crm-auth.${token}`])
        this.socket = socket

        socket.onopen = () => {
            if (this.socket !== socket) {
                socket.close()
                return
            }

            this.scheduleReconnectReset(socket)
            this.sendSubscriptions([...this.handlers.keys()])
            this.startHeartbeat()
        }

        socket.onmessage = (event) => {
            if (this.socket !== socket) return
            this.handleMessage(event.data)
        }

        socket.onclose = (event) => {
            void this.handleClose(socket, event)
        }

        socket.onerror = () => {
            socket.close()
        }
    }

    disconnect = () => {
        this.isManualDisconnect = true
        this.clearReconnectTimeout()
        this.clearReconnectResetTimeout()
        this.stopHeartbeat()
        this.socket?.close()
        this.socket = null
    }

    isConnected = () => this.socket?.readyState === WebSocket.OPEN

    sendPresenceHeartbeat = () => {
        if (!this.isConnected()) return false

        this.sendHeartbeat()
        return true
    }

    private unsubscribe = (topic: RealtimeTopic, handler: RealtimeHandler) => {
        const topicHandlers = this.handlers.get(topic)

        if (!topicHandlers) return

        topicHandlers.delete(handler)

        if (topicHandlers.size === 0) {
            this.handlers.delete(topic)
            this.sendMessage({ type: 'unsubscribe', topics: [topic] })
        }

        if (this.handlers.size === 0) {
            this.disconnect()
        }
    }

    private handleMessage = (rawMessage: string) => {
        let message: RealtimeEvent

        try {
            message = JSON.parse(rawMessage)
        } catch {
            reportUnexpectedClientError(new Error('Realtime-сервис вернул некорректный JSON.'), 'RealtimeService.handleMessage')
            return
        }

        if (message.type !== 'event' || !message.topic) {
            reportUnexpectedClientError(new Error('Realtime-сервис вернул сообщение некорректной структуры.'), 'RealtimeService.handleMessage')
            return
        }

        this.handlers.get(message.topic)?.forEach((handler) => handler(message))
    }

    private handleClose = async (socket: WebSocket, event: CloseEvent) => {
        if (this.socket !== socket) return

        this.clearReconnectResetTimeout()
        this.stopHeartbeat()

        if (event.code === 4001) {
            let isRefreshed: boolean
            try {
                isRefreshed = await tokenService.refreshTokenRequest()
            } catch (error) {
                reportUnexpectedClientError(error, 'RealtimeService.handleClose')
                this.socket = null
                if (!this.isManualDisconnect && this.handlers.size > 0) this.scheduleReconnect()
                return
            }

            if (this.socket !== socket) return

            if (!isRefreshed) {
                tokenService.clearTokensAndRedirect()
                return
            }
        }

        this.socket = null

        if (!this.isManualDisconnect && this.handlers.size > 0) {
            this.scheduleReconnect()
        }
    }

    private sendSubscriptions = (topics: RealtimeTopic[]) => {
        if (topics.length === 0) return

        this.sendMessage({ type: 'subscribe', topics })
    }

    private startHeartbeat = () => {
        this.stopHeartbeat()
        this.sendHeartbeat()

        this.heartbeatIntervalID = setInterval(() => {
            this.sendHeartbeat()
        }, 25000)
    }

    private sendHeartbeat = () => {
        this.sendMessage({
            type: 'heartbeat',
            ...getCurrentRoutePageMeta(),
        })
    }

    private stopHeartbeat = () => {
        if (this.heartbeatIntervalID) {
            clearInterval(this.heartbeatIntervalID)
            this.heartbeatIntervalID = null
        }
    }

    private scheduleReconnect = () => {
        this.clearReconnectTimeout()

        const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempt))
        this.reconnectAttempt += 1

        this.reconnectTimeoutID = setTimeout(() => {
            this.connect()
        }, delay)
    }

    private clearReconnectTimeout = () => {
        if (this.reconnectTimeoutID) {
            clearTimeout(this.reconnectTimeoutID)
            this.reconnectTimeoutID = null
        }
    }

    private scheduleReconnectReset = (socket: WebSocket) => {
        this.clearReconnectResetTimeout()
        this.reconnectResetTimeoutID = setTimeout(() => {
            if (this.socket === socket && socket.readyState === WebSocket.OPEN) {
                this.reconnectAttempt = 0
            }
            this.reconnectResetTimeoutID = null
        }, RealtimeService.RECONNECT_RESET_DELAY_MS)
    }

    private clearReconnectResetTimeout = () => {
        if (this.reconnectResetTimeoutID) {
            clearTimeout(this.reconnectResetTimeoutID)
            this.reconnectResetTimeoutID = null
        }
    }

    private sendMessage = (message: unknown) => {
        if (this.socket?.readyState !== WebSocket.OPEN) return

        this.socket.send(JSON.stringify(message))
    }

    private getRealtimeUrl = () => {
        const url = new URL(APP_API_URL)
        const normalizedPath = url.pathname.replace(/\/$/, '')

        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
        url.pathname = normalizedPath.endsWith('/api')
            ? normalizedPath.replace(/\/api$/, '/ws')
            : `${normalizedPath}/ws`
        url.search = ''
        url.hash = ''

        return url.toString()
    }
}

const realtimeService = new RealtimeService()

export default realtimeService